// Shared surface styling and lookups for the Aria coach page and its panels.

import { GoalScope, GoalSource } from '../../../../../../pure-js/types/CoachTypes'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { COMPONENT_DIMENSIONS } from '../../../../constants/layout'

// The card surface lives in shared/coachPreferences because the preferences section uses it too.
export {
  coachCardSx,
  coachCardPaddingSx,
  coachCardWithPaddingSx
} from '../../../../shared/coachPreferences/coachSurfaceStyles'

/** Where a goal came from, as a translation key. */
export const GOAL_SOURCE_KEY: Record<GoalSource, string> = {
  [GoalSource.ONBOARDING]: 'gamer.ariaCoach.goalSource.onboarding',
  [GoalSource.COACH_FLOW]: 'gamer.ariaCoach.goalSource.coach',
  [GoalSource.FACILITATOR]: 'gamer.ariaCoach.goalSource.facilitator',
  [GoalSource.SYSTEM]: 'gamer.ariaCoach.goalSource.system'
}

/** The heading of the goal list of each coaching focus, as a translation key. */
export const GOAL_LIST_HEADING_KEYS: Record<GoalScope, string> = {
  [GoalScope.INDIVIDUAL]: 'gamer.ariaCoach.yourGoals',
  [GoalScope.LEADERSHIP]: 'gamer.ariaCoach.goals.leadershipTitle'
}

/** What an empty goal list of each coaching focus says, as a translation key. */
export const GOAL_LIST_EMPTY_KEYS: Record<GoalScope, string> = {
  [GoalScope.INDIVIDUAL]: 'gamer.ariaCoach.noGoals',
  [GoalScope.LEADERSHIP]: 'gamer.ariaCoach.goals.noLeadershipGoals'
}

export type CoachStartCopy = { title: string; body: string; cta: string }

/** What a conversation with a job of its own says it is for. Keys stay literal so `yarn check-translations` sees them. */
export function resolveCoachStartCopy(
  t: (key: string) => string,
  flags: { isFirstGoalSetting: boolean; isLeadershipFocus: boolean }
): CoachStartCopy | undefined {
  // A leader's first goal-setting is about leadership goals, and the focus is already the leadership one then.
  if (flags.isFirstGoalSetting && flags.isLeadershipFocus) {
    return {
      title: t('gamer.ariaCoach.leadership.goalSettingTitle'),
      body: t('gamer.ariaCoach.leadership.goalSettingBody'),
      cta: t('gamer.ariaCoach.goalSettingCta')
    }
  }

  if (flags.isFirstGoalSetting) {
    return {
      title: t('gamer.ariaCoach.goalSettingTitle'),
      body: t('gamer.ariaCoach.goalSettingBody'),
      cta: t('gamer.ariaCoach.goalSettingCta')
    }
  }

  if (flags.isLeadershipFocus) {
    return {
      title: t('gamer.ariaCoach.leadership.emptyTitle'),
      body: t('gamer.ariaCoach.leadership.emptyBody'),
      cta: t('gamer.ariaCoach.leadership.startCta')
    }
  }

  return undefined
}

export const COACH_FOCUS_LABEL_KEYS: Record<GoalScope, string> = {
  [GoalScope.INDIVIDUAL]: 'gamer.ariaCoach.focus.individual',
  [GoalScope.LEADERSHIP]: 'gamer.ariaCoach.focus.leadership'
}

export const COACH_FOCUS_ICON_SIZE_PX = COMPONENT_DIMENSIONS.ICON_SM

const COACH_FOCUS_TRANSITION_MS = 160 // 0.16 s

/** The focus switch is a labelled pill, where the mode switch beside it is icons only. */
export const coachFocusButtonSx = {
  ...INTERAX_TYPOGRAPHY.body4,
  px: 1.5,
  py: 0.75,
  gap: 0.75,
  fontWeight: 600,
  textTransform: 'none',
  transition: `background ${COACH_FOCUS_TRANSITION_MS}ms ease, color ${COACH_FOCUS_TRANSITION_MS}ms ease`
}

/** Width of the goals + conversations column on desktop; the conversation takes whatever is left. */
export const COACH_SIDE_PANEL_WIDTH_PX = 320

/** Folded, the same panel is an icon rail — wide enough for a 40px icon button and its padding. */
export const COACH_SIDE_PANEL_RAIL_WIDTH_PX = 56

/** Long enough for the fold to read as the column moving; short enough not to make anyone wait for it. */
export const COACH_SIDE_PANEL_FOLD_MS = 220 // 0.22 s

/**
 * The fold: the column changes width, and whatever replaced the previous content fades up into it. The
 * two halves are separate because the width lives on the wrapper and the fade on whichever branch mounted.
 */
export const coachSidePanelFoldSx = {
  transition: `width ${COACH_SIDE_PANEL_FOLD_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
}

export const coachSidePanelContentSx = {
  animation: `coachSidePanelContentIn ${COACH_SIDE_PANEL_FOLD_MS}ms ease-out both`,
  '@keyframes coachSidePanelContentIn': {
    '0%': { opacity: 0 },
    '40%': { opacity: 0 },
    '100%': { opacity: 1 }
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
}

/** How long the session feedback holds a highlight when it opens over a conversation. */
export const FEEDBACK_ATTENTION_MS = 1600 // 1.6 s

/** A ring that fades out rather than a flash — it opens over a conversation already in progress. */
export const coachFeedbackAttentionSx = {
  borderRadius: 3,
  animation: `coachFeedbackAttention ${FEEDBACK_ATTENTION_MS}ms ease-out 1`,
  '@keyframes coachFeedbackAttention': {
    '0%': { boxShadow: `0 0 0 0 ${INTERAX_COLORS.sun40}`, transform: 'scale(0.995)' },
    '25%': { boxShadow: `0 0 0 6px ${INTERAX_COLORS.sun20}`, transform: 'scale(1)' },
    '100%': { boxShadow: '0 0 0 0 rgba(0,0,0,0)', transform: 'scale(1)' }
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
}

/** Bigger than the floating panel's, small enough that it does not dominate the card. */
export const COACH_VOICE_BUTTON_SIZE_PX = 44

/** Voice-only: the bulb is the whole point of the screen, so it runs large. */
export const COACH_BULB_SIZE_PX = 220

/** With captions on, the bulb yields the screen to the conversation. */
export const COACH_BULB_SIZE_WITH_SUBTITLES_PX = 84

/** Captions read as a column, not a banner — roughly the width of a paragraph of prose. */
export const COACH_SUBTITLE_MAX_WIDTH_PX = 560
export const COACH_MESSAGE_MAX_WIDTH = '78%'
